import {
	type Campaign,
	type GMAppContext,
	type TreeReference,
	useAppContext,
} from '@datapad/data-model';
import { assertNotUndefined } from '@datapad/utilities';
import { Box, Divider, Stack } from '@mui/material';
import React from 'react';
import { DatapadAppStateContext } from '../../../../utilities';
import { AppId } from '../../AppId';
import { AppSelectionList } from './AppSelectionList';
import { CampaignDetails } from './CampaignDetails';
import { CampaignSelector } from './CampaignSelector';
import { CharacterSelector } from './CharacterSelector';
import { menuWidth } from './Constants';
import { MenuFooter } from './MenuFooter';
import { MenuHeader } from './MenuHeader';
import { ResourceLinksList } from './ResourceLinksList';

/**
 * {@link DatapadMenu} input props.
 */
export interface DatapadMenuProps {
	/**
	 * Selected application.
	 */
	readonly appSelection: AppId;

	/**
	 * Function for signing the user out of the application.
	 */
	logout(): void;

	/**
	 * Hides (collapses) the menu view.
	 */
	hideMenu(): void;

	/**
	 * Callback to change the session character.
	 */
	changeSessionCharacter(character: TreeReference | undefined): void;

	/**
	 * Callback to change the session campaign.
	 */
	changeSessionCampaign(campaign: TreeReference | undefined): void;
}

/**
 * Datapad menu component - the slide-out navigation drawer.
 *
 * @remarks
 * The menu uses a modern, clean layout with:
 * - Prominent header with user identity
 * - Logical section groupings without heavy dividers
 * - Subtle visual separators (light dividers) between major sections
 * - Consistent spacing throughout
 *
 * The layout uses a flex column that allows the middle content to scroll
 * while keeping the header and footer visible.
 */
export function DatapadMenu(props: DatapadMenuProps): React.ReactElement {
	const { hideMenu, appSelection, logout, changeSessionCharacter, changeSessionCampaign } = props;

	const appContext = useAppContext();
	const { changeAppSelection } = assertNotUndefined(React.useContext(DatapadAppStateContext));

	// Get current campaign if available
	let currentCampaign: Campaign | undefined;
	if (
		appContext.permissionLevel === 'guest' ||
		appContext.getEffectivePermissionLevel() === 'guest'
	) {
		currentCampaign = undefined;
	} else {
		currentCampaign =
			appContext.campaign === undefined
				? undefined
				: appContext.getCampaign(appContext.campaign);
	}

	// Get player's characters if not a guest
	const characters =
		appContext.permissionLevel === 'game-master'
			? appContext.getAllCharacters()
			: appContext.getUsersCharacters();

	// Get current character reference
	const currentCharacter =
		appContext.permissionLevel !== 'guest' ? appContext.character : undefined;

	// Get all campaigns the current character belongs to
	const characterCampaigns = React.useMemo(() => {
		if (appContext.permissionLevel === 'guest' || currentCharacter === undefined) {
			return [];
		}
		// TODO: Add helper to AppContext for getting a character's campaigns
		return appContext
			.getAllCampaigns()
			.filter((campaign) =>
				[...campaign.activeCharacters.values()].some(
					(ref) => ref.id === currentCharacter.id,
				),
			);
	}, [appContext, currentCharacter]);

	// Actual permission level (used for showing GM-only features like View As toggle)
	const isActualGameMaster = appContext.permissionLevel === 'game-master';

	// Effective permission level (used for UI visibility based on View As mode)
	const effectivePermissionLevel = appContext.getEffectivePermissionLevel();
	const isEffectiveGuest = effectivePermissionLevel === 'guest';
	const isEffectiveGameMaster = effectivePermissionLevel === 'game-master';

	return (
		<Stack
			component="nav"
			direction="column"
			sx={{
				/*
				 * Fixed width ensures consistent menu appearance.
				 * Full height with flex column allows scrollable content area.
				 * overflow: hidden prevents content from extending beyond bounds.
				 */
				width: menuWidth,
				minWidth: menuWidth,
				maxWidth: menuWidth,
				height: '100%',
				overflow: 'hidden',
			}}
		>
			{/* Header - fixed at top */}
			<MenuHeader hideMenu={hideMenu} />

			{/*
			 * Scrollable content area.
			 * flex: 1 fills available space, overflow: auto enables scrolling.
			 * minWidth: 0 allows flex children to shrink below content size.
			 */}
			<Box
				sx={{
					flex: 1,
					minWidth: 0,
					overflowY: 'auto',
					overflowX: 'hidden',
				}}
			>
				{/* Character selector (non-guests with multiple characters only) */}
				{!isEffectiveGuest && characters.length > 1 && (
					<CharacterSelector
						characters={characters}
						currentCharacter={currentCharacter}
						changeSelectedCharacter={(character) => {
							changeSessionCharacter(character);
							changeAppSelection(AppId.Profile, undefined, 'open');
						}}
					/>
				)}

				{/* Campaign selector (only when character belongs to multiple campaigns) */}
				{!isEffectiveGuest && characterCampaigns.length > 1 && (
					<CampaignSelector
						campaigns={characterCampaigns}
						currentCampaign={'campaign' in appContext ? appContext.campaign : undefined}
						changeSelectedCampaign={changeSessionCampaign}
					/>
				)}

				{/* Campaign date (if available) */}
				{currentCampaign !== undefined && (
					<CampaignDetails
						campaign={currentCampaign}
						onClick={() => {
							if (currentCampaign.currentDate !== undefined) {
								changeAppSelection(
									AppId.Calendar,
									currentCampaign.currentDate.toUrlSearch(),
								);
							}
							hideMenu();
						}}
					/>
				)}

				{/*
				 * Light divider creates subtle visual separation
				 * without the heaviness of full-width borders.
				 */}
				<Divider sx={{ mx: 2 }} />

				{/* App selection list (uses effective permission level for View As mode) */}
				<AppSelectionList
					isGuest={isEffectiveGuest}
					isGameMaster={isEffectiveGameMaster}
					currentAppSelection={appSelection}
					changeAppSelection={changeAppSelection}
				/>

				<Divider sx={{ mx: 2 }} />

				{/* External resource links */}
				<ResourceLinksList />
			</Box>

			{/*
			 * Footer - fixed at bottom.
			 * Subtle top border separates it from scrollable content.
			 */}
			<Box sx={{ borderTop: 1, borderColor: 'divider' }}>
				<MenuFooter
					logout={logout}
					isGameMaster={isActualGameMaster}
					effectivePermissionLevel={effectivePermissionLevel}
					setEffectivePermissionLevel={
						(appContext as GMAppContext).setEffectivePermissionLevel
					}
				/>
			</Box>
		</Stack>
	);
}
